/**
 * Rabies Vaccination Certificate (US) — interactive form for populating rabies
 * vaccine details before generating the PDF.
 */
import { SNOOTS_US } from '@snootsvet/shared'

import { Input } from '@/frontend/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { PatientDetailsPreview } from '@/frontend/features/printing/documents/blocks/patient-details'
import { VetSignoffPreview } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import {
  defaultRabiesVaccinationCertificateFields,
  type RabiesVaccinationCertificateFields,
  RabiesVaccinationCertificatePdf,
  type RabiesVaccineDuration,
} from '@/frontend/features/printing/documents/rabies-vaccination-certificate.print'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

// Base UI Select needs an `items` prop so the trigger can resolve the selected
// value's label without the dropdown content being mounted.
const durationItems: { label: string; value: RabiesVaccineDuration }[] = [
  { label: '3 years', value: '3-year' },
  { label: '1 year', value: '1-year' },
]

/** Number of years each rabies vaccine validity adds to the vaccination date. */
const DURATION_YEARS: Record<RabiesVaccineDuration, number> = { '1-year': 1, '3-year': 3 }

/** Formats a Date as an ISO YYYY-MM-DD string from its local date parts. */
function toIsoDate(date: Date): string {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/** Today's date as an ISO YYYY-MM-DD string (local time). */
function todayIso(): string {
  return toIsoDate(new Date())
}

/**
 * Adds whole years to an ISO date string, clamping the day to the last valid
 * day of the target month (e.g. 2024-02-29 + 1 year → 2025-02-28). Returns ''
 * for empty or unparseable input.
 */
function addYearsIso(iso: string, years: number): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return ''
  // Day 0 of month (m + years' month index + 1) resolves to the last day of the target month.
  const lastDayOfMonth = new Date(y + years, m, 0).getDate()
  return toIsoDate(new Date(y + years, m - 1, Math.min(d, lastDayOfMonth)))
}

/**
 * Recomputes `nextDueDate` as the vaccination date plus the selected validity
 * period. Call whenever `dateVaccinated` or `duration` changes so the due date
 * stays in sync.
 */
function withRecomputedNextDue(
  fields: RabiesVaccinationCertificateFields
): RabiesVaccinationCertificateFields {
  return {
    ...fields,
    nextDueDate: addYearsIso(fields.dateVaccinated, DURATION_YEARS[fields.duration]),
  }
}

/**
 * Builds the initial field values at editor mount: date vaccinated defaults to
 * today and next-due is derived from it, so a fresh certificate opens ready to
 * generate. Computed lazily (not at module load) so "today" is correct.
 */
function makeDefaultRabiesVaccinationCertificateFields(): RabiesVaccinationCertificateFields {
  return withRecomputedNextDue({
    ...defaultRabiesVaccinationCertificateFields,
    dateVaccinated: todayIso(),
  })
}

function RabiesVaccinationCertificateForm({
  context,
  value,
  onChange,
}: DocumentFormProps<RabiesVaccinationCertificateFields>) {
  const { patient, clinic } = context

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} showName />

      <h1 className="mb-4 text-center text-lg">Rabies Vaccination Certificate</h1>

      <OwnerDetailsPreview owner={context.owner} />
      <div className="mt-3">
        <PatientDetailsPreview
          patient={patient}
          weight={context.weight}
          showAge
          showWeight
          showMicrochip
        />
      </div>

      <p className="mt-4">
        This is to certify that {patient.name} has received a rabies vaccination as detailed below.
      </p>

      {/* Rabies vaccine details */}
      <div className="mt-4">
        <h2 className="text-headline-md">Rabies Vaccination:</h2>
        <ul className="mt-3 list-disc space-y-3 pl-6">
          <li className="flex flex-wrap items-center gap-2">
            Product name:
            <Input
              className="w-64"
              value={value.productName}
              onChange={(e) => onChange({ ...value, productName: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Manufacturer:
            <Input
              className="w-64"
              value={value.manufacturer}
              onChange={(e) => onChange({ ...value, manufacturer: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Serial (lot) number:
            <Input
              className="w-64"
              value={value.serialNumber}
              onChange={(e) => onChange({ ...value, serialNumber: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Date vaccinated:
            <Input
              type="date"
              className="w-48"
              value={value.dateVaccinated}
              // Changing the vaccination date shifts the next-due date with it.
              onChange={(e) =>
                onChange(withRecomputedNextDue({ ...value, dateVaccinated: e.target.value }))
              }
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Expiry date:
            <Input
              type="date"
              className="w-48"
              value={value.expiryDate}
              onChange={(e) => onChange({ ...value, expiryDate: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Vaccine validity:
            <Select
              value={value.duration}
              // Changing the validity re-derives the next-due date.
              onValueChange={(v) =>
                onChange(
                  withRecomputedNextDue({
                    ...value,
                    duration: (v ?? '3-year') as RabiesVaccineDuration,
                  })
                )
              }
              items={durationItems}
            >
              <SelectTrigger className="inline-flex w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationItems.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Next vaccination due:
            {/* Auto-filled from date vaccinated + validity; still editable for edge cases. */}
            <Input
              type="date"
              className="w-48"
              value={value.nextDueDate}
              onChange={(e) => onChange({ ...value, nextDueDate: e.target.value })}
            />
          </li>
        </ul>
      </div>

      <p className="mt-6">
        Please feel free to contact us for any further information or clarification. On behalf of
        Snoots Vet.
      </p>

      <VetSignoffPreview
        vetName={value.vetName}
        onChange={(v) =>
          onChange({ ...value, vetName: v.vetName, vetLicenseNumber: v.licenseNumber ?? '' })
        }
        label="Dr"
        showLicenseNumber
        registrationLabel="License number"
        showSignature
        showDate
      />
    </DocumentPreview>
  )
}

export const rabiesVaccinationCertificate = withDocumentEditor({
  id: 'rabies-vaccination-certificate',
  label: 'Rabies Vaccination Certificate (US)',
  // US-specific document — always issued from Snoots Clifton (NJ).
  clinic: SNOOTS_US,
  defaultFields: makeDefaultRabiesVaccinationCertificateFields,
  Preview: RabiesVaccinationCertificateForm,
  Pdf: RabiesVaccinationCertificatePdf,
})
